import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance — withCredentials enables httpOnly cookie auth
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Handle 401 errors — redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Avoid redirect loop on /auth/me check during initial load
      const isAuthCheck = error.config?.url?.includes('/auth/me');
      if (!isAuthCheck && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Stats Pub (admin)
export const statsAdsAPI = {
  get: () => api.get('/admin/stats-ads'),
};

export const abonnesAnnuelsAPI = {
  get: () => api.get('/admin/abonnes-annuels'),
};

export const commercialStatsAPI = {
  get: (days) => api.get(`/admin/commercial-stats?days=${days}`),
};

export const adminJournalAPI = {
  ipn: (mode = 'all', limit = 200) => api.get(`/admin/paypal-ipn-journal?mode=${mode}&limit=${limit}`),
  exportCsv: (dateFrom, dateTo, mode = 'all') => api.get(`/admin/paypal-ipn-journal/export.csv?date_from=${dateFrom || ''}&date_to=${dateTo || ''}&mode=${mode}`, { responseType: 'blob' }),
  emails: (limit = 100) => api.get(`/admin/email-deliveries?limit=${limit}`),
  retryEmail: (id) => api.post(`/admin/email-deliveries/${id}/retry`),
  ackAlert: (id) => api.post(`/admin/alerts/${id}/ack`),
  renewals: () => api.get('/admin/renewals'),
  checkRenewals: () => api.post('/admin/renewals/check'),
  syncRenewal: (subscriptionId) => api.post(`/admin/renewals/${subscriptionId}/sync`),
  reminderStatus: () => api.get('/admin/renewal-reminders/status'),
  reminderDryRun: () => api.post('/admin/renewal-reminders/run?dry_run=true'),
};

export const paypalSandboxAPI = {
  liveCheck: () => api.get('/admin/paypal-live-check'),
  status: (verify = false) => api.get(`/admin/paypal-sandbox/status${verify ? '?verify=true' : ''}`),
  saveConfig: (data) => api.post('/admin/paypal-sandbox/config', data),
  createPlans: () => api.post('/admin/paypal-sandbox/create-plans'),
  toggle: (enabled) => api.post('/admin/paypal-sandbox/toggle', { enabled }),
  testEmail: (to_email) => api.post('/admin/test-email', { to_email }),
};

// Leads
export const leadsAPI = {
  create: (data) => api.post('/leads', data),
  getAll: () => api.get('/leads'),
};

// Factures
export const facturesAPI = {
  create: (data) => api.post('/factures', data),
  getAll: () => api.get('/factures'),
  update: (id, data) => api.put(`/factures/${id}`, data),
  delete: (id) => api.delete(`/factures/${id}`),
  addPaiement: (id, data) => api.post(`/factures/${id}/paiement`, data),
  downloadPDF: async (id) => {
    const response = await api.get(`/factures/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Facture_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Devis
export const devisAPI = {
  create: (data) => api.post('/devis', data),
  getAll: () => api.get('/devis'),
  update: (id, data) => api.put(`/devis/${id}`, data),
  delete: (id) => api.delete(`/devis/${id}`),
  convert: (id) => api.post(`/devis/${id}/convert`),
};

// Dépenses
export const depensesAPI = {
  create: (data) => api.post('/depenses', data),
  getAll: () => api.get('/depenses'),
  update: (id, data) => api.put(`/depenses/${id}`, data),
  delete: (id) => api.delete(`/depenses/${id}`),
  updateStatut: (id, statut) => api.put(`/depenses/${id}/statut?statut=${statut}`),
  addPaiement: (id, data) => api.post(`/depenses/${id}/paiement`, data),
  downloadPDF: async (id) => {
    const response = await api.get(`/depenses/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Depense_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Auth settings + Profil entreprise
export const userAPI = {
  updateSettings: (data) => api.put('/auth/settings', data),
  updateEntreprise: (data) => api.put('/auth/settings', data),
};

// Taux
export const tauxAPI = {
  get: () => api.get('/taux'),
  update: (data) => api.put('/taux', data),
};

// Simulations
export const simulationsAPI = {
  urssaf: (data) => api.post('/simulations/urssaf', data),
  epargneRetraite: (data) => api.post('/simulations/epargne-retraite', data),
  protectionSociale: (data) => api.post('/simulations/protection-sociale', data),
  checklistAssurance: (data) => api.post('/simulations/checklist-assurance', data),
};

// Dashboard
export const dashboardAPI = {
  getStats: (month) => api.get('/dashboard/stats', { params: { month } }),
};

// Bonus
export const bonusAPI = {
  getAll: () => api.get('/bonus'),
};

export default api;
